import { jwtDecode } from 'jwt-decode';
import { KEYS } from './constants/storage.constants';

const COUNTRY_CODES = {
  INDIA: 'IN',
} as const;

/**
 * Interface for location data
 */
interface LocationData {
  countryCode: string;
}

/**
 * @method isLoggedIn
 * @returns boolean
 *
 * Checks whether the user is logged in or not
 */
export const isLoggedIn = (): boolean => {
  const accessToken = localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
  return (
    accessToken !== null && accessToken !== undefined && accessToken.length > 0
  );
};

/**
 * Sets an expiration time
 *
 * @param mstime - Duration in milliseconds in which the expiration time will be set
 * @param key - Name that will be used to store the expiration time
 * @param bufferMs - Optional buffer in milliseconds to refresh token early (default: 60000ms = 60 seconds)
 */
export const setExpirationTime = (mstime: number, key: string, bufferMs: number = 60000): void => {
  if (mstime <= 0) {
    throw new Error('Expiration time must be a positive number');
  }

  try {
    const expirationTime = Date.now() + mstime - bufferMs;

    // Validation: ensure expiration time is valid and reasonable
    if (isNaN(expirationTime) || expirationTime < Date.now()) {
      console.error('Invalid expiration time calculated:', { mstime, bufferMs, expirationTime });
      throw new Error('Invalid expiration time calculation');
    }

    localStorage.setItem(key, expirationTime.toString());
  } catch (error) {
    console.error('Error setting expiration time:', error);
    throw error;
  }
};

/**
 * Validates an expiration time
 * Note: The expiration timestamp already includes the buffer, so tokens
 * are considered expired 60 seconds before their actual expiry time.
 * This allows proactive token refresh before the token truly expires.
 *
 * @param key - Name that will be used to retrieve the expiration time
 * @returns True if the expiration time has expired (including buffer) or False if not
 */
export const expTimeHasExpired = (key: string): boolean => {
  try {
    const expirationTime = localStorage.getItem(key);
    if (!expirationTime) return true;

    const currentTime = Date.now();
    const expirationTimestamp = Number(expirationTime);

    if (isNaN(expirationTimestamp)) {
      console.error('Invalid expiration time format');
      return true;
    }
    return currentTime > expirationTimestamp;
  } catch (error) {
    console.error('Error checking expiration time:', error);
    return true;
  }
};

/**
 * Clears all login-related data from storage
 */
export const clearLoginData = (): void => {
  try {
    localStorage.removeItem(KEYS.EVERGENT_ACCESS_TOKEN);
    localStorage.removeItem(KEYS.LOCATION);
    localStorage.removeItem(KEYS.EVERGENT_ACCESS_TOKEN_EXPIRY);
  } catch (error) {
    console.error('Error clearing login data:', error);
  }
};

/**
 * Gets the remaining time until expiration
 * @param key - Storage key for the expiration time
 * @returns number - Remaining time in milliseconds, or 0 if expired
 */
export const getRemainingTime = (key: string): number => {
  try {
    const expirationTime = localStorage.getItem(key);
    if (!expirationTime) return 0;

    const expirationTimestamp = Number(expirationTime);
    if (isNaN(expirationTimestamp)) return 0;

    const currentTime = Date.now();
    return Math.max(0, expirationTimestamp - currentTime);
  } catch (error) {
    console.error('Error getting remaining time:', error);
    return 0;
  }
};

/**
 * Decodes a JWT token with structural validation
 * @param token - The JWT token string to decode
 * @returns any | null - Decoded payload or null if invalid
 */
export const decodeToken = (token: string): any | null => {
  if (
    !token ||
    token === 'undefined' ||
    token === 'null' ||
    token.split('.').length !== 3
  ) {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Decodes a token and saves its expiry to localStorage
 * @param token - The JWT token string
 * @returns number | null - The expiry timestamp or null if failed
 */
export const saveTokenExpiry = (token: string): number | null => {
  const decoded = decodeToken(token);
  if (decoded && decoded.exp) {
    try {
      localStorage.setItem(
        KEYS.EVERGENT_ACCESS_TOKEN_EXPIRY,
        decoded.exp.toString()
      );
      return decoded.exp;
    } catch (error) {
      console.error('Error saving token expiry:', error);
    }
  }
  return null;
};
